import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Intercom, { boot, update, shutdown } from '@intercom/messenger-js-sdk';
import { AccountType } from '@enview/interface/types/Organization';
import { State } from '../dux/@types';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import intercomAPI from '../api/intercom';

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_ID ?? '';

const IntercomWrapper = (): null => {
  const authToken = useSelector((state: State) => state.authentication.authToken);
  const organizationUser = useSelector((state: State) => state.account.organizationUser);
  const booted = useRef(false);

  const { data: intercomJwt } = intercomAPI.endpoints.getIntercomUserJwt.useQuery(
    undefined,
    { skip: !authToken },
  );

  const mapIntercomData = (organizationUser: OrganizationUser) => {
    return {
        app_id: INTERCOM_APP_ID,
        user_id: String(organizationUser.id),
        name: `${organizationUser.firstName} ${organizationUser.lastName}`,
        email: organizationUser.email,
        created_at: Math.floor(new Date(organizationUser.createdAt).getTime() / 1000),
        intercom_user_jwt: intercomJwt?.token,
        isProOrgMember: organizationUser.organizations.some(
          (org) => org.accountStatus === AccountType.Pro,
        ),
        isTrialOrgMember: organizationUser.organizations.some(
          (org) => org.accountStatus === AccountType.Trial,
        ),
        isStarterOrgMember: organizationUser.organizations.some(
          (org) => org.accountStatus === AccountType.Starter,
        ),
      }
  }

  useEffect(() => {
    if (!INTERCOM_APP_ID || INTERCOM_APP_ID === 'testing') return;
    Intercom({ app_id: INTERCOM_APP_ID });
  }, []);

  useEffect(() => {
    if (!INTERCOM_APP_ID) return;

    if (authToken && organizationUser && intercomJwt?.token && !booted.current) {
      boot(mapIntercomData(organizationUser));
      booted.current = true;
    }
    else if (authToken && organizationUser && intercomJwt?.token && booted.current) {
      update(mapIntercomData(organizationUser));
    }
    else if (!authToken && booted.current) {
      shutdown();
      booted.current = false;
    }
  }, [authToken, organizationUser, intercomJwt]);

  return null;
};

export default IntercomWrapper;
